import { DdsRefs } from "@/components/dds";
import { useCallback, useEffect, useState } from "react";
import { Loader2, ShieldAlert, Trash2, Undo2 } from "lucide-react";
import { DeleteMaterialDialog } from "@/components/MaterialDelete";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CodeHistory } from "@/components/CodeHistory";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api, errMsg, fmtDate, fmtMoney, fmtNum } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ScopeBadge } from "@/components/common";
import { PoPlant } from "@/components/PoPlant";
import { CodeBadge, CodeDialog, EditCodeButton } from "@/components/CodeEdit";

function MiniTable({ cols, rows, testid }) {
  if (!rows.length) return <p className="py-8 text-center text-sm text-slate-500">No records.</p>;
  return (
    <div className="scroll-thin overflow-x-auto rounded-lg border border-slate-200" data-testid={testid}>
      <table className="w-full text-xs">
        <thead className="bg-slate-50">
          <tr>{cols.map((c) => <th key={c[0]} className="whitespace-nowrap px-3 py-2 text-left font-semibold uppercase tracking-wider text-slate-500">{c[0]}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r, i) => (
            <tr key={i}>{cols.map((c) => <td key={c[0]} className="whitespace-nowrap px-3 py-2 text-slate-700">{c[1](r)}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function OverrideDialog({ rec, onClose, onDone }) {
  const [scope, setScope] = useState("in_scope");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => { setReason(""); setErr(""); setScope(rec?.scope === "in_scope" ? "out_of_scope" : "in_scope"); }, [rec]);
  const save = async () => {
    if (!reason.trim()) return setErr("A reason is required to override the scope.");
    setBusy(true);
    try {
      await api.post(`/materials/${rec.id}/override`, { scope, reason });
      toast.success(`Scope overridden for ${rec.material} / ${rec.plant}`);
      onDone();
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={!!rec} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="override-dialog">
        <DialogHeader><DialogTitle>Override scope · {rec?.material} @ {rec?.plant}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">Auto match: <ScopeBadge scope={rec?.scope_auto} /> The override survives re-imports and Annex I changes.</p>
        <label className="mt-2 text-xs font-semibold uppercase tracking-wider text-slate-500">New scope</label>
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger data-testid="override-scope-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="in_scope" data-testid="override-scope-in_scope">In scope</SelectItem>
            <SelectItem value="out_of_scope" data-testid="override-scope-out_of_scope">Out of scope</SelectItem>
            <SelectItem value="check" data-testid="override-scope-check">Check</SelectItem>
          </SelectContent>
        </Select>
        <label className="mt-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Reason (required)</label>
        <Textarea data-testid="override-reason-input" value={reason} onChange={(e) => { setReason(e.target.value); setErr(""); }} placeholder="e.g. Solid oak desk – wooden furniture per supplier spec sheet" rows={3} />
        {err && <p className="text-sm text-rose-600" data-testid="override-error">{err}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="override-save-btn" onClick={save} disabled={busy}>{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save override</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PlantCard({ r, can, onOverride, onClear, onDelete }) {
  return (
    <div className="rounded-xl border border-slate-200 p-4" data-testid={`material-plant-card-${r.plant}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-500">Plant</p>
          <p className="font-mono text-lg font-semibold text-slate-900">{r.plant}</p>
        </div>
        <ScopeBadge scope={r.scope} from={r.applies_from} overridden={!!r.override} testid={`drawer-scope-${r.plant}`} />
        {r.annex_condition && <span className="text-xs text-slate-500" data-testid={`drawer-condition-${r.plant}`}>{r.annex_condition}</span>}
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
        <dt className="text-slate-500">Commodity code</dt><dd className="font-mono" data-testid={`drawer-code-${r.plant}`}>{r.commodity_code || "—"}<CodeBadge r={r} testid={`drawer-code-badge-${r.plant}`} /></dd>
        {r.code_correction && <><dt className="text-slate-500">SAP code</dt><dd className="font-mono" data-testid={`drawer-sap-code-${r.plant}`}>{r.sap_commodity_code || "—"} <span className="font-sans text-slate-500">· corrected by {r.code_correction.by}{r.code_correction.reason ? ` — “${r.code_correction.reason}”` : ""}</span></dd></>}
        <dt className="text-slate-500">Annex I match</dt><dd className="font-mono">{r.annex_code || "—"} {r.commodity ? `· ${r.commodity}` : ""}</dd>
        <dt className="text-slate-500">Unrestricted stock</dt><dd>{fmtNum(r.unrestricted_stock)}</dd>
        <dt className="text-slate-500">SAP status</dt><dd>{r.status || "—"} / {r.plant_status || "—"}</dd>
        <dt className="text-slate-500">DDS refs</dt><dd><DdsRefs refs={r.dds_refs} testid={`material-dds-${r.plant}`} /></dd>
      </dl>
      {r.annex_description && <p className="mt-2 text-xs text-slate-500">{r.annex_description}</p>}
      {r.override && (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid={`override-info-${r.plant}`}>
          <p className="flex items-center gap-1.5 font-semibold"><ShieldAlert className="h-3.5 w-3.5" /> Overridden by {r.override.user_name || r.override.user} · {fmtDate(r.override.at, true)}</p>
          <p className="mt-1">“{r.override.reason}”</p>
        </div>
      )}
      {can.override && (
        <div className="mt-3 flex gap-2">
          <Button size="sm" variant="outline" onClick={() => onOverride(r)} data-testid={`override-btn-${r.plant}`}><ShieldAlert className="mr-1.5 h-3.5 w-3.5" /> Override scope</Button>
          {r.override && r.scope !== "not_required" && <Button size="sm" variant="ghost" onClick={() => onClear(r)} data-testid={`clear-override-btn-${r.plant}`}><Undo2 className="mr-1.5 h-3.5 w-3.5" /> Revert to auto</Button>}
          <Button size="sm" variant="ghost" className="ml-auto text-rose-600 hover:bg-rose-50" onClick={() => onDelete(r)} data-testid={`drawer-delete-btn-${r.plant}`}><Trash2 className="mr-1.5 h-3.5 w-3.5" /> Delete</Button>
        </div>
      )}
    </div>
  );
}

export default function MaterialDrawer({ material, onClose, onChanged }) {
  const { can } = useAuth();
  const [d, setD] = useState(null);
  const [ov, setOv] = useState(null);
  const [del, setDel] = useState(null);
  const [code, setCode] = useState(null);
  const load = useCallback(() => {
    if (!material) return;
    api.get(`/materials/detail/${encodeURIComponent(material)}`).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [material]);
  useEffect(() => { setD(null); load(); }, [load]);
  const clear = async (r) => {
    await api.delete(`/materials/${r.id}/override`);
    toast.success("Override removed");
    load();
    onChanged();
  };
  return (
    <Sheet open={!!material} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-3xl" data-testid="material-drawer">
        <SheetHeader>
          <SheetTitle className="font-display">
            <span className="font-mono">{material}</span>
            <span className="ml-2 text-base font-normal text-slate-500">{d?.description}</span>
          </SheetTitle>
        </SheetHeader>
        {!d ? (
          <div className="flex justify-center py-20"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : (
          <div className="mt-5 space-y-5">
            {can.upload && (
              <div className="flex items-center gap-2">
                <EditCodeButton testid="drawer-edit-code-btn" onClick={() => setCode({ ...d.plants[0], material })} />
                {d.plants.some((p) => p.code_correction) && <Button size="sm" variant="ghost" data-testid="drawer-revert-code-btn" onClick={async () => { await api.delete(`/materials/by-number/${encodeURIComponent(material)}/code`); toast.success("Reverted to SAP code"); load(); onChanged(); }}><Undo2 className="mr-1.5 h-3.5 w-3.5" /> Revert to SAP code</Button>}
              </div>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              {d.plants.map((r) => <PlantCard key={r.id} r={r} can={can} onOverride={setOv} onClear={clear} onDelete={setDel} />)}
            </div>
            <Tabs defaultValue="stock">
              <TabsList>
                <TabsTrigger value="stock" data-testid="drawer-tab-stock">Stock ({d.stock.length})</TabsTrigger>
                <TabsTrigger value="vendors" data-testid="drawer-tab-vendors">Vendors ({d.vendors.length})</TabsTrigger>
                <TabsTrigger value="po" data-testid="drawer-tab-po">PO lines ({d.po_total})</TabsTrigger>
                <TabsTrigger value="sales" data-testid="drawer-tab-sales">Sales ({d.sales.length})</TabsTrigger>
                <TabsTrigger value="codes" data-testid="drawer-tab-code-history">Code history</TabsTrigger>
              </TabsList>
              <TabsContent value="codes"><CodeHistory material={material} /></TabsContent>
              <TabsContent value="stock">
                <MiniTable testid="drawer-stock-table" rows={d.stock} cols={[["Plant", (r) => r.plant], ["SLoc", (r) => r.storage_location || "—"], ["Unrestricted", (r) => fmtNum(r.unrestricted)], ["UoM", (r) => r.uom], ["Value", (r) => fmtMoney(r.value_unrestricted, r.currency || "GBP")], ["In transit", (r) => fmtNum(r.transit)], ["Blocked", (r) => fmtNum(r.blocked)]]} />
              </TabsContent>
              <TabsContent value="vendors">
                <MiniTable testid="drawer-vendors-table" rows={d.vendors} cols={[["Vendor", (r) => <span className="font-mono">{r.vendor}</span>], ["Name", (r) => r.name], ["Country", (r) => r.country], ["PO lines", (r) => r.lines], ["Value GBP", (r) => fmtMoney(r.value_gbp)], ["Last PO", (r) => fmtDate(r.last_po)]]} />
              </TabsContent>
              <TabsContent value="po">
                <MiniTable testid="drawer-po-table" rows={d.po_lines} cols={[["PO", (r) => <span className="font-mono">{r.po_no}</span>], ["Date", (r) => fmtDate(r.po_date)], ["Vendor", (r) => r.vendor_name], ["Ctry", (r) => r.vendor_country], ["Plant", (r) => <PoPlant r={r} testid={`drawer-po-plant-${r.record_id || r.id}`} />], ["Qty", (r) => `${fmtNum(r.po_qty)} ${r.uom || ""}`], ["Value", (r) => fmtMoney(r.value, r.currency || "GBP")], ["GBP", (r) => fmtMoney(r.value_gbp)]]} />
              </TabsContent>
              <TabsContent value="sales">
                <MiniTable testid="drawer-sales-table" rows={d.sales} cols={[["Billing doc", (r) => r.billing_doc], ["Item", (r) => r.item], ["Date", (r) => fmtDate(r.billing_date)], ["Sold-to", (r) => r.sold_to], ["Country", (r) => r.country], ["Qty", (r) => fmtNum(r.qty)], ["Net", (r) => fmtMoney(r.net_value, r.currency || "GBP")]]} />
              </TabsContent>
            </Tabs>
          </div>
        )}
        <DeleteMaterialDialog rec={del} onClose={() => setDel(null)} onDone={() => { setDel(null); onChanged(); if (d && d.plants.length > 1) load(); else onClose(); }} />
        <CodeDialog rec={code} onClose={() => setCode(null)} onDone={() => { setCode(null); load(); onChanged(); }} />
        <OverrideDialog rec={ov} onClose={() => setOv(null)} onDone={() => { setOv(null); load(); onChanged(); }} />
      </SheetContent>
    </Sheet>
  );
}
